import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type { BeaconEvent, TrackedBeaconEvent } from '@/apis/types';

interface ActivityBeaconState {
  pending: TrackedBeaconEvent[];
}

// An audit queue that grows without bound on a long-lived tab is a worse
// failure than dropping the oldest, least-relevant events.
export const MAX_PENDING_EVENTS = 50;

const initialState: ActivityBeaconState = { pending: [] };

function newTrackingId(): string {
  // randomUUID is only available in secure contexts (https / localhost).
  return typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : nanoid();
}

/**
 * Activity events the backend hasn't confirmed yet. Persisted by
 * redux-persist, so events survive a refresh / tab close and are sent on the
 * next load (see useActivityBeacon).
 */
const activityBeaconSlice = createSlice({
  name: 'activityBeacon',
  initialState,
  reducers: {
    eventAdded: {
      reducer: (state, action: PayloadAction<TrackedBeaconEvent>) => {
        state.pending.push(action.payload);
        state.pending = state.pending.slice(-MAX_PENDING_EVENTS);
      },
      prepare: (event: BeaconEvent) => ({
        payload: { ...event, tracking_id: newTrackingId() },
      }),
    },
    eventsRemoved: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      state.pending = state.pending.filter(
        (event) => !ids.has(event.tracking_id)
      );
    },
  },
});

export const { eventAdded, eventsRemoved } = activityBeaconSlice.actions;
export default activityBeaconSlice.reducer;
